import React from 'react';
import { Shield, Calendar, Database, Clock, Users, Cookie, Mail, AlertCircle, FileText, Building2, User, Info } from 'lucide-react';

const MentionsRGPD = () => {
  return (
    <div className="min-h-screen  w-full bg-gradient-to-br from-blue-50 to-indigo-50 2xl:text-lg xl:text-base">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* En-tête */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-blue-600 rounded-2xl">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Mentions d'information RGPD</h1>
          <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
        </div>

        {/* Objet du traitement */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-blue-100 rounded-xl">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">Objet du traitement</h2>
          </div>
          
          <div className="space-y-4 text-gray-600">
            <p className="leading-relaxed">
              La société <strong className="text-gray-900">SAS TANOED</strong>, dont le siège social est situé au 50 Bis Route Nationale 2 97412 BRAS-PANON, 
              propose une plateforme numérique éducative accessible via le GAR.
            </p>
            
            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl">
              <p className="text-gray-700">
                <strong className="text-blue-900">Le GAR (Gestionnaire d'Accès aux Ressources)</strong> est un service sécurisé d'accès 
                aux ressources numériques pour les collèges, lycées et écoles primaires fourni par le ministère de l'Éducation Nationale.
              </p>
            </div>
            
            <p className="leading-relaxed">
              La plateforme numérique TanoEd s'inscrit dans le cadre du Traitement GAR, placé sous la responsabilité du ministre 
              chargé de l'Éducation Nationale. Les règles applicables par défaut sont celles indiquées dans les mentions d'information 
              RGPD du GAR et sont accessibles à l'adresse : 
              <a href="https://gar.education.fr/mentions-informatives-rgpd" target="_blank" rel="noopener noreferrer" 
                 className="text-blue-600 hover:text-blue-800 transition-colors ml-1">
                https://gar.education.fr/mentions-informatives-rgpd
              </a>
            </p>
            
            <div className="mt-6">
              <p className="font-medium text-gray-800 mb-3">L'identification de l'utilisateur est déléguée au GAR.</p>
              <div className="bg-gray-50 p-6 rounded-xl">
                <p className="text-sm text-gray-700 mb-3">Le GAR transmet à SAS TANOED les données suivantes :</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[UAI] Code établissement</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[IDO] Identifiant opaque</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[idENT] Code projet de l'ENT</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[PRO] Profil</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[DIV] Division(s)</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[GRO] Groupe(s)</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[NOM] Nom d'usage</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[PRE] Prénom usuel</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[P_MAT] Données enseignant</span>
                </div>
              </div>
            </div>
            
            <p className="leading-relaxed mt-4">
              La finalité de cette transmission est la communication des données strictement nécessaires aux accès des élèves 
              et des enseignants à la ressource et à son utilisation en fonction des droits qui leur sont ouverts.
            </p>
          </div>
        </section>

        {/* Bases légales */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center  gap-3 mb-6">
            <div className="p-3 bg-green-100 rounded-xl">
              <Info className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">Bases légales et finalité des traitements</h2>
          </div>
          
          <p className="text-gray-600 leading-relaxed">
            Les bases légales et finalités sont celles du traitement GAR : cf. 
            <a href="https://gar.education.fr/mentions-informatives-rgpd" target="_blank" rel="noopener noreferrer" 
               className="text-blue-600 hover:text-blue-800 transition-colors ml-1">
              https://gar.education.fr/mentions-informatives-rgpd
            </a>
          </p>
        </section>

        {/* Catégories de données */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Database className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">Catégories de données et durée de conservation</h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600 leading-relaxed">
              Dans le cadre de son intégration au Gestionnaire d'Accès aux Ressources (GAR), TanoEd ne collecte et ne traite 
              que les données personnelles strictement nécessaires à son fonctionnement pédagogique, conformément au principe 
              de minimisation du GAR. Les catégories de données traitées sont les suivantes :
            </p>
            
            <div className="space-y-4">
              {[
                {
                  title: "Résultats d'évaluations des élèves",
                  description: "Données issues des évaluations nationales et en classe, organisées par élève, par classe et par compétence (notes, niveaux de maîtrise, etc.), afin de suivre la progression de chaque élève.",
                  icon: "📊"
                },
                {
                  title: "Affectations pédagogiques",
                  description: "Informations d'affectation des enseignants à leurs classes (liaison entre un enseignant et les classes dont il a la charge).",
                  icon: "👥"
                },
                {
                  title: "Composition des classes",
                  description: "Listes des élèves rattachés à chaque classe, c'est-à-dire le peuplement des classes nécessaire pour afficher aux enseignants la liste de leurs élèves.",
                  icon: "📚"
                },
                {
                  title: "Informations d'établissement",
                  description: "Données d'identification de l'établissement scolaire de l'utilisateur (principalement le nom de l'établissement et son code UAI, identifiant unique de l'école).",
                  icon: "🏫"
                },
                {
                  title: "Données techniques de connexion",
                  description: "Logs de connexion incluant l'identifiant de l'utilisateur (fourni via le GAR) et l'horodatage des accès (date et heure de connexion), éventuellement l'adresse IP de connexion, dans le but d'assurer la sécurité et le suivi technique du service.",
                  icon: "🔐"
                }
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{item.icon}</span>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800 mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-xl mt-6">
              <div className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-orange-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Durée de conservation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Toutes les catégories de données ci-dessus sont conservées uniquement pour la durée de l'année scolaire en cours. 
                    Une <strong>purge complète est effectuée chaque année le 15 août</strong>, de sorte que les données de l'année écoulée 
                    (y compris les listes d'élèves par classe, les affectations et les résultats d'évaluation) sont intégralement 
                    supprimées à cette date.
                  </p>
                  <p className="text-gray-700 mt-2">
                    Les données de connexion (logs) sont également limitées à une conservation maximale d'environ 12 mois et sont 
                    purgées à la même échéance annuelle, conformément aux bonnes pratiques du GAR.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cookies */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-yellow-100 rounded-xl">
              <Cookie className="w-6 h-6 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">Utilisation des cookies</h2>
          </div>
          
          <div className="space-y-3">
            <p className="text-gray-600 leading-relaxed">
              La plateforme TanoEd utilise exclusivement des cookies techniques nécessaires pour gérer et sécuriser 
              votre connexion (token d'accès utilisateur).
            </p>
            <div className="bg-green-50 p-4 rounded-xl flex items-center gap-3">
              <Shield className="w-5 h-5 text-green-600" />
              <p className="text-gray-700 font-medium">
                Les données enregistrées par les cookies et traceurs utilisés ne sont EN AUCUN CAS utilisées à des fins commerciales.
              </p>
            </div>
          </div>
        </section>

        {/* Destinataires */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <Users className="w-6 h-6 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">Destinataires des données</h2>
          </div>
          
          <div className="space-y-3 ">
            <p className="text-gray-600">
              La société <strong className="text-gray-900">SAS TANOED est l'unique destinataire</strong> des données personnelles collectées.
            </p>
            <p className="text-gray-600">
              SAS TANOED n'a recours à aucun sous-traitant ayant accès à ces données.
            </p>
            <p className="text-gray-600">
              Les données des utilisateurs ne sont en aucun cas transmises à d'autres entités.
            </p>
          </div>
        </section>

        {/* Vos droits */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-red-100 rounded-xl">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">Vos droits : droits d'accès, de rectification et de limitation</h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600 leading-relaxed">
              Les demandes d'exercice de vos droits peuvent être adressées à SOCIETE TANOED :
            </p>
            
            <div className="grid gap-3 bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <p className="text-gray-700">
                  Par voie électronique : <a href="mailto:mangata@tanoed.com" className="text-blue-600 hover:text-blue-800 transition-colors">mangata@tanoed.com</a>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-blue-600 mt-0.5" />
                <p className="text-gray-700">
                  Par voie postale : 50 Bis Route Nationale 2, Appt 6, Immeuble Mangata, 97412 Bras-Panon
                </p>
              </div>
            </div>
            
            <p className="text-gray-600">
              Vous pouvez aussi contacter le GAR : cf. 
              <a href="https://gar.education.fr/mentions-informatives-rgpd" target="_blank" rel="noopener noreferrer" 
                 className="text-blue-600 hover:text-blue-800 transition-colors ml-1">
                https://gar.education.fr/mentions-informatives-rgpd
              </a>
            </p>
            
            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl">
              <p className="text-gray-700">
                Si vous estimez après nous avoir contacté que les droits sur vos données n'ont pas été respectés, 
                vous pouvez introduire une réclamation auprès de la <strong>CNIL</strong>.
              </p>
              <p className="text-gray-700 mt-2">
                Voir le site de la CNIL pour plus d'informations sur vos droits et pour transmettre une réclamation au CNIL.
              </p>
            </div>
          </div>
        </section>

        {/* Responsables */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gray-100 rounded-xl">
              <User className="w-6 h-6 text-gray-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">Responsables</h2>
          </div>
          
          <div className="space-y-6">
            <div className="border-l-4 border-blue-600 pl-6">
              <h3 className="font-semibold text-gray-800 mb-2">Responsable du traitement des données personnelles</h3>
              <p className="text-gray-600">
                Ministère de l'Éducation Nationale. cf. 
                <a href="https://gar.education.fr/mentions-informatives-rgpd" target="_blank" rel="noopener noreferrer" 
                   className="text-blue-600 hover:text-blue-800 transition-colors ml-1">
                  https://gar.education.fr/mentions-informatives-rgpd
                </a>
              </p>
            </div>
            
            <div className="border-l-4 border-green-600 pl-6">
              <h3 className="font-semibold text-gray-800 mb-2">Éditeur de l'application</h3>
              <div className="space-y-1 text-gray-600">
                <p><strong>SOCIETE TANOED</strong></p>
                <p>Siège social : 50 Bis Route Nationale 2 97412 Bras-Panon</p>
                <p>SIREN : 935059899 - Type de société : SAS</p>
                <p>N° TVA intracommunautaire : FR43935059899</p>
                <p>Téléphone : +262 0692 26 51 74</p>
                <p>E-mail : <a href="mailto:mangata@tanoed.com" className="text-blue-600 hover:text-blue-800 transition-colors">mangata@tanoed.com</a></p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-sm text-gray-600 mb-1">Délégué à la protection des données</p>
                <p className="font-semibold text-gray-800">MANGATA Nicky</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-sm text-gray-600 mb-1">Directeur de la publication</p>
                <p className="font-semibold text-gray-800">MANGATA Nicky</p>
              </div>
            </div>
          </div>
        </section>
          <div className="text-center mt-12">
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Calendar className="w-4 h-4" />
            <p className="text-sm">
              <strong>Dernière mise à jour :</strong> Juillet 2025
            </p>
          </div>
        </div>
      </div>
         {/* Pied de page */}
      
    </div>
  );
};

export default MentionsRGPD;